import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Clock, Play, Radio, Tv } from "lucide-react";
import axios from "axios";
import { ASNLoader } from "./ASNLoader";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/* ── YouTube video ID extractor ── */
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  const m = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/live\/)([^&?/]+)/
  );
  return m ? m[1] : null;
};

/* ── Compact YouTube embed (click-to-play, thumbnail-first) ── */
const CompactEmbed = ({ broadcast }) => {
  const [playing, setPlaying] = useState(false);
  const videoId = getYouTubeVideoId(broadcast?.youtube_url);
  const thumb =
    broadcast?.thumbnail_url ||
    (videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null);

  if (!videoId) {
    return (
      <div className="aspect-video bg-zinc-900 flex items-center justify-center max-w-2xl mx-auto">
        <span className="text-zinc-600 text-sm">No video available yet</span>
      </div>
    );
  }

  if (!playing) {
    return (
      <div
        className="aspect-video bg-zinc-900 relative cursor-pointer group max-w-2xl mx-auto overflow-hidden"
        onClick={() => setPlaying(true)}
        data-testid="channel-embed-thumb"
      >
        {thumb && (
          <img src={thumb} alt="" className="w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors flex items-center justify-center">
          <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
            <Play className="w-8 h-8 text-white fill-white ml-0.5" />
          </div>
        </div>
        {/* Title overlay */}
        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 to-transparent p-4">
          <p className="text-white font-bold text-sm truncate">{broadcast.title}</p>
          <p className="text-zinc-400 text-xs mt-0.5">
            {formatDate(broadcast.scheduled_date, broadcast.scheduled_time)}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="aspect-video bg-black max-w-2xl mx-auto" data-testid="channel-embed-player">
      <iframe
        src={`https://www.youtube.com/embed/${videoId}?autoplay=1&modestbranding=1&rel=0`}
        title="Broadcast"
        className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
};

/* ── Date formatter ── */
const formatDate = (date, time) => {
  if (!date) return "";
  try {
    const d = new Date(`${date}T${time || "00:00"}`);
    return (
      d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) +
      " · " +
      d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })
    );
  } catch {
    return date;
  }
};

/* ── Schedule row ── */
const ScheduleRow = ({ broadcast, channelColor }) => {
  const home = broadcast.home_team || {};
  const away = broadcast.away_team || {};
  const hasTeams = home.name && away.name;

  const content = (
    <div
      className="flex items-center gap-4 py-3 px-4 border-b border-white/5 last:border-0 hover:bg-white/[0.04] transition-colors cursor-pointer"
      data-testid={`schedule-row-${broadcast.id}`}
    >
      {/* Date / time */}
      <div className="w-28 shrink-0 text-xs text-zinc-500">
        <div className="flex items-center gap-1.5">
          <Calendar className="w-3 h-3" />
          {broadcast.scheduled_date
            ? new Date(broadcast.scheduled_date + "T00:00").toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              })
            : "TBD"}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <Clock className="w-3 h-3" />
          {broadcast.scheduled_time
            ? (() => {
                const [h, m] = broadcast.scheduled_time.split(":");
                const hr = parseInt(h);
                return `${hr % 12 || 12}:${m} ${hr >= 12 ? "PM" : "AM"}`;
              })()
            : "TBD"}
        </div>
      </div>

      {/* Teams / title */}
      <div className="flex-1 min-w-0">
        {hasTeams ? (
          <div className="flex items-center gap-2">
            {away.logo_url && (
              <img src={away.logo_url} alt="" className="w-5 h-5 object-contain shrink-0" />
            )}
            <span className="text-white text-sm font-medium truncate">
              {away.name}
            </span>
            <span className="text-zinc-600 text-xs">@</span>
            <span className="text-white text-sm font-medium truncate">
              {home.name}
            </span>
            {home.logo_url && (
              <img src={home.logo_url} alt="" className="w-5 h-5 object-contain shrink-0" />
            )}
          </div>
        ) : (
          <span className="text-white text-sm">{broadcast.title}</span>
        )}
        {broadcast.sport && (
          <span className="text-[10px] text-zinc-600 uppercase tracking-wider">{broadcast.sport}</span>
        )}
      </div>

      {/* Status badge */}
      {broadcast.is_live ? (
        <span className="flex items-center gap-1 text-[10px] font-bold text-red-500 uppercase shrink-0">
          <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" /> LIVE
        </span>
      ) : broadcast.is_completed ? (
        <span className="text-[10px] text-zinc-600 uppercase shrink-0">Final</span>
      ) : null}
    </div>
  );

  // Link to the game page for this broadcast
  const gameUrl = `/game/${broadcast.id}`;
  return <Link to={gameUrl} className="block no-underline">{content}</Link>;
};

/* ── Main Page ── */
export const ChannelHomePage = () => {
  const { channelId } = useParams();
  const [channel, setChannel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/channels/${channelId}`)
      .then((res) => setChannel(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [channelId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <ASNLoader size="fullscreen" />
      </div>
    );
  }

  if (!channel) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <Tv className="w-14 h-14 text-zinc-700 mx-auto mb-3" />
          <h2 className="text-white text-lg font-bold mb-2">Channel Not Found</h2>
          <Link to="/channels" className="text-zinc-500 hover:text-white text-sm">
            ← Back to Channels
          </Link>
        </div>
      </div>
    );
  }

  const color = channel.primary_color || "#FFFFFF";
  const allBroadcasts = [
    ...(channel.upcoming_broadcasts || []),
  ].filter((b) => !b.is_completed);

  // Next upcoming broadcast (first non-live upcoming, or current live)
  const nextBroadcast =
    channel.current_broadcast ||
    (channel.upcoming_broadcasts || []).find((b) => b.youtube_url) ||
    (channel.upcoming_broadcasts || [])[0];

  return (
    <div className="min-h-screen bg-[#050505]">
      <main className="pt-6 pb-20 px-4 md:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Back link */}
          <Link
            to="/channels"
            className="inline-flex items-center gap-2 text-zinc-500 hover:text-white text-sm mb-6 transition-colors"
            data-testid="back-to-channels"
          >
            <ArrowLeft className="w-4 h-4" /> Channels
          </Link>

          {/* Channel header */}
          <div className="flex items-center gap-4 mb-8">
            {channel.logo_url ? (
              <img src={channel.logo_url} alt="" className="h-14 w-auto object-contain" />
            ) : (
              <div
                className="w-14 h-14 flex items-center justify-center bg-zinc-900 border border-white/10"
                style={{ color }}
              >
                <span className="text-2xl font-black font-secondary">{channel.name?.[0]}</span>
              </div>
            )}
            <div>
              <h1 className="font-secondary text-2xl sm:text-3xl font-bold uppercase text-white">
                {channel.name}
              </h1>
              {channel.is_live && (
                <span className="flex items-center gap-1.5 text-xs font-bold text-red-500 uppercase mt-1">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  Live Now
                </span>
              )}
            </div>
          </div>

          {/* Channel Banners */}
          {(channel.banners || []).filter(b => b.is_active !== false).length > 0 && (
            <div className="mb-8 space-y-3" data-testid="channel-banners">
              {channel.banners.filter(b => b.is_active !== false).map((banner) => (
                <div key={banner.id} className="w-full overflow-hidden">
                  {banner.link_url ? (
                    <a href={banner.link_url} target="_blank" rel="noopener noreferrer">
                      <img src={banner.image_url} alt={banner.name || ""} className="w-full h-auto object-cover hover:opacity-90 transition-opacity" />
                    </a>
                  ) : (
                    <img src={banner.image_url} alt={banner.name || ""} className="w-full h-auto object-cover" />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Next broadcast embed */}
          {nextBroadcast ? (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-10"
              data-testid="next-broadcast-embed"
            >
              <h2 className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-3">
                {channel.is_live ? "Now Playing" : "Next Broadcast"}
              </h2>
              <CompactEmbed broadcast={nextBroadcast} />
            </motion.div>
          ) : (
            <div className="text-center py-12 mb-8 bg-zinc-900/30 border border-white/5">
              <Radio className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">No upcoming broadcasts scheduled</p>
            </div>
          )}

          {/* Schedule list */}
          {allBroadcasts.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              data-testid="channel-schedule-list"
            >
              <h2 className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-3">
                Schedule
              </h2>
              <div className="bg-zinc-900/40 border border-white/5">
                {allBroadcasts.map((b) => (
                  <ScheduleRow key={b.id} broadcast={b} channelColor={color} />
                ))}
              </div>
            </motion.div>
          )}

          {/* Past broadcasts */}
          {(channel.past_broadcasts || []).length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="mt-10"
            >
              <h2 className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-3">
                Recent
              </h2>
              <div className="bg-zinc-900/40 border border-white/5 opacity-60">
                {channel.past_broadcasts.slice(0, 5).map((b) => (
                  <ScheduleRow key={b.id} broadcast={b} channelColor={color} />
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};

export default ChannelHomePage;
